'use strict';

import axios from 'axios';
import {
    Group,
    MathUtils,
    Vector3,
} from 'three';
import {isMobile, resourcesFolder, settingsData, threeJsVariables} from '../../../settings';
import { createHtmlElement, labelVisibility } from '../../../functions';
import {lordIconColors, setLordIcon} from '../../../lordicon';
import { CSS2DObject } from '../../../libs/CSS2DRenderer';
import {
    addBuildingsIntersections,
    addPoiToIntersection,
    isBuildingIntersectionsSet,
    poiIntersection,
    removePoiFromIntersection,
} from '../../poi/poiCss';
import { Route } from '../../poi/Route';
import { GoogleMaps } from '../../api/GoogleMaps';
import { changeLangToCurrent, getCurrentLang } from '../langSwitcher/langSwitcher';
import { SelectedObjectType } from '../../../common';
import { PublicLayers } from './PublicLayers';
import {Cloudflare} from "../../api/Cloudflare";
import {BaseElement} from "../../interfaces/BaseElement";
import { OrbitDayAndNight } from '../../liveAnimations/DayNight/OrbitDayAndNight';


export class Layer extends Route {
    constructor (parent,layer) {
        super();

        if (!layer.active) return;

        this.parent = parent;
        this.layer = layer;
        this.type = this.layer.type;
        this.active = false;
        this.url = `${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/points/${this.layer.file}?v=${Cloudflare.version}`;
        this.animations = [];
        this.labels = [];
        this.iconColors = {};

        this.btn = createHtmlElement(this.parent,'','button',['public-layer__btn',`${this.layer.type}`]);
        this.btn.setAttribute('data-public-layer',this.layer.type);

        this.#getIconColors();
        setLordIcon(this.layer.type,this.btn,{
            trigger: 'hover',
            stroke: 70
        });

        this.btnText = createHtmlElement(this.btn,'','span',['public-layer__btn-text', 'language-string']);
        this.btnText.setAttribute('data-dictionary', this.type.charAt(0).toUpperCase() + this.type.slice(1));
        this.btnText.innerHTML = changeLangToCurrent(this.type.charAt(0).toUpperCase() + this.type.slice(1));

        this.btn.addEventListener('mouseover', this.mouseOverHandler.bind(this));
        this.btn.addEventListener('mouseout', this.mouseOutHandler.bind(this));

        this.routeGroup = new Group();
        this.routeGroup.name = this.layer.type;

        const { position,rotation,scale } = this.layer;

        this.routeGroup.position.set(position.x,position.y,position.z);
        this.routeGroup.rotation.set(rotation.x,rotation.y,rotation.z);
        this.routeGroup.scale.set(scale.x,scale.y,scale.z);

        this.response = this.#getRouteData();

        this.animateCamera = false;

        return this;
    }

    #getIconColors() {
        // const { primaryColor, secondaryColor, activeColor } = getLordIconColor();

        this.iconColors.primaryColor = lordIconColors.primary;
        this.iconColors.secondaryColor = lordIconColors.secondary;
        this.iconColors.activeColor = lordIconColors.active;
    }

    #getRouteData() {

        return new Promise(async (resolve) => {

            let data;

            try {
                const response = await axios.get(this.url);
                data = response.data.elements;
            }catch (e) {
                data = null;
            }

            resolve(data);
        });
    }

    mouseOverHandler() {
       !this.active && this.enable();
    }

    mouseOutHandler() {
       !this.active && this.disable();
    }

    update(delta) {
        this.active && this.animations.forEach(el => el.tick(delta));
    }

    show() {
        if (this.active) return;

        threeJsVariables.scene.add(this.routeGroup);
        threeJsVariables.renderList.reorder();

        !isBuildingIntersectionsSet() && addBuildingsIntersections();
        
        if (!OrbitDayAndNight.isActiveAndSelected) {
            this.hideLabelsByDistance(true);
        }

        this.routeGroup.traverse(el => {
           if (el.type === 'Object3D' && el.name.startsWith('layer-poi')) {
               const text = el.element.querySelector('.public-label__text') || el.element.querySelector('.public-label__name') ;
               if (text) {
                   text.innerText = text.getAttribute(`data-lang-${getCurrentLang()}`);
               }
               addPoiToIntersection(el);
               poiIntersection();
           } else if (el.isMesh && el.userData.type === SelectedObjectType.Route) {
               settingsData.objects_to_intersection.push(el);
           }
        });


        this.active = true;
        this.enable();
    }
    
    hideLabelsByDistance(state) {
        const labels = this.labels.filter(label => label.distance > 400);
        labels.forEach(data => labelVisibility(data,state));
    }

    hide() {
        this.routeGroup.removeFromParent();

        this.routeGroup.traverse(el => {
            if (!el.isGroup) {

                if (el.type === 'Object3D' && el.name.startsWith('layer-poi')){
                    el.element.remove();
                    removePoiFromIntersection(el);
                } else {
                    el.geometry.dispose();
                    el.material.dispose();
                    settingsData.objects_to_intersection = settingsData.objects_to_intersection.filter(intersectedObject => intersectedObject !== el);
                }
            }
        });
        this.animations = this.animations.filter(material => material.name !== 'route_dash');
        this.removeRoute();
        this.active = false;
        this.disable();
        PublicLayers.scope.hidePopup();
    }

    disable() {
        if (this.btn.classList.contains('deals'))
            return;

        const icon = this.btn.querySelector('.lord__icon');
        icon.setAttribute('colors',`primary:${this.iconColors.primaryColor},secondary:${this.iconColors.secondaryColor}`);
        this.btn.style.color = '';
    }

    enable() {
        if (this.btn.classList.contains('deals'))
            return;

        const icon = this.btn.querySelector('.lord__icon');
        icon.setAttribute('colors',`primary:${this.iconColors.activeColor},secondary:${this.iconColors.activeColor}`);
        this.btn.style.color = this.iconColors.activeColor;
    }

    async poiHandler(poiData) {
        let route;

        if (poiData.route) {
            route = poiData.route;
        } else {
            route = await GoogleMaps.getRoute(poiData.from,poiData.to,'WALKING');
            poiData.route = route;
        }

        if (isMobile || config.features?.publicLayers?.buildRoutes === false)
            return;

        const dashLine = this.drawRoute(route,poiData.id);
        this.animations.push(dashLine);

    }

}

export function createLabel (names, icon, color,distance) {
    // const labelContainer = createHtmlElement('','','div',['public-label']);
    const labelContainer = new BaseElement()
        .addClass('public-label');

    let iconLabel;
    if (icon.length === 1) {
        // iconLabel = createHtmlElement(labelContainer.DOMElement,icon,'div',['public-label__type']);
        iconLabel = new BaseElement()
            .addClass('public-label__type')
            .innerHtml(icon)
            .appendTo(labelContainer.DOMElement);
    } else {
        // iconLabel = createHtmlElement(labelContainer.DOMElement,'','div',['public-label__type',`${icon}`]);
        iconLabel = new BaseElement()
            .addClass('public-label__type',`${icon}`)
            .appendTo(labelContainer.DOMElement);
    }

    // const textBlock = createHtmlElement(labelContainer.DOMElement,'','div',['public-label__text-block']);
    const textBlock = new BaseElement()
        .addClass('public-label__text-block')
        .appendTo(labelContainer.DOMElement);
    // const text = createHtmlElement(textBlock.DOMElement,`${names[getCurrentLang()]}`,'span',['public-label__text','language-string']);
    const text = new BaseElement('span')
        .addClass('public-label__text','language-string')
        .innerHtml(`${names[getCurrentLang()]}`)
        .appendTo(textBlock.DOMElement);
    Object.entries(names).forEach(([key,value]) => {
        text.addAttribute(`data-lang-${key}`,value);
    });

    // createHtmlElement(textBlock.DOMElement,'|','span',['public-label-text__separator']);
    new BaseElement('span')
        .addClass('public-label-text__separator')
        .innerHtml('|')
        .appendTo(textBlock.DOMElement);
    // createHtmlElement(textBlock.DOMElement,`${distance}m`,'span',['public-label__distance']);
    new BaseElement('span')
        .addClass('public-label__distance')
        .appendTo(textBlock.DOMElement)
        .innerHtml(`${distance}m`)
        .appendTo(textBlock.DOMElement);

    if (color)
        iconLabel.addStyle('background', color);

    const label = new CSS2DObject(labelContainer.DOMElement);
    label.userData.zoom = 1;
    label.name = `layer-poi__${name}`;

    return label;
}

export function createDealLabel (data) {
    const point = createHtmlElement('','','div',['deals-point', 'hidden']);
    const labelContainer = createHtmlElement(point,'','div',['deals-label']);
    const content = createHtmlElement(labelContainer,'','div',['deals-content']);
    const top = createHtmlElement(content,'','div',['deals-content-top']);
    const bottom = createHtmlElement(content,'','div',['deals-content-bottom']);
    let currency;
    switch (config.localization.currencyLabel) {
        case 'EUR':
            currency = '€';
            break;
        case 'USD':
            currency = '$';
            break;
        case 'UAH':
            currency = '₴';
            break;
        case 'NIS':
            currency = '₪';
            break;
    }
    // const price = createHtmlElement(top,numberWithCommas(data.price,'.') + ` ${currency}`,'span',['deals-currency']);
    new BaseElement('span')
        .addClass('deals-currency')
        .innerHtml(changeLangToCurrent(data.price) + ` ${currency}`)
        .appendTo(top);

    Object.entries(data).forEach(([key,value]) => {
       if (key !== 'coords' && key !=='price') {
           // const item = createHtmlElement(bottom,'','div',['deals-item']);
           // const textValue = createHtmlElement(item,value,'span',['deals-item-value']);
           // const subText = createHtmlElement(item,changeLangToCurrent(key),'span',['deals-item-sub-text','language-string']);
           // subText.setAttribute('data-dictionary',key);

           const item = new BaseElement()
               .addClass('deals-item')
               .appendTo(bottom);
           new BaseElement('span')
               .addClass('deals-item-value')
               .innerHtml(value)
               .appendTo(item.DOMElement);
           new BaseElement('span')
               .addClass('deals-item-sub-text','language-string')
               .innerHtml(changeLangToCurrent(key))
               .addAttribute('data-dictionary', key)
               .appendTo(item.DOMElement);
       }
    });

    const label = new CSS2DObject(point);
    label.userData.zoom = 1;
    label.name = `layer-poi__deals`;

    return label;
}

export function createNameLabel (names) {

    const labelContainer = createHtmlElement('','','div',['public-label']);
    const text = createHtmlElement(labelContainer,`${names[getCurrentLang()]}`,'span',['public-label__name','language-string']);

    Object.entries(names).forEach(([key,value]) => {
        text.setAttribute(`data-lang-${key}`,value);
    });

    const label = new CSS2DObject(labelContainer);
    label.userData.zoom = 1;
    label.name = `layer-poi__${names[getCurrentLang()]}`;

    return label;
}



// Calculate vector 3 from lat lng

// const EARTH_RADIUS_METERS = 6378137; // Previously radius
const EARTH_RADIUS_METERS = 6270000; // Set custom earth radius

const {sin, cos, pow, sqrt, atan2,sign} = Math;
const {degToRad } = MathUtils;


export function latLngAltToVector3(point, reference, axis = new Vector3(0,1,0).normalize(),radius = EARTH_RADIUS_METERS) {
    const dz = distance(reference, {lng: point.lng, lat: reference.lat},radius);
    const dx = distance(reference, {lng: reference.lng, lat: point.lat},radius);

    const sz = sign(point.lng - reference.lng);
    const sx = sign(point.lat - reference.lat);
    const v = new Vector3(sx * dx, 0, sz * dz);
    v.applyAxisAngle(axis,Math.PI);

    return v;
}

function distance(from, to,radius){
    const {lat: latFrom, lng: lngFrom} = from;
    const {lat: latTo, lng: lngTo} = to;

    const dLat = degToRad(latTo - latFrom);
    const dLon = degToRad(lngTo - lngFrom);
    const lat1 = degToRad(latFrom);
    const lat2 = degToRad(latTo);

    const a = pow(sin(dLat / 2), 2) + pow(sin(dLon / 2), 2) * cos(lat1) * cos(lat2);

    return 2 * atan2(sqrt(a), sqrt(1 - a)) * radius;
}
